import {
  CommonModule,
  isPlatformBrowser,
  isPlatformServer,
} from '@angular/common';
import {
  APP_INITIALIZER,
  PLATFORM_ID,
  InjectionToken,
  NgModule,
  TransferState,
  makeStateKey,
} from '@angular/core';
import { MemoryStorage, OAuthModule } from 'angular-oauth2-oidc';

import { AUTH_ISSUER_KEY } from '~/auth/providers/abstract-auth.provider';
import { CoreModule } from '~/core/core.module';
import { CustomHeadersEnum } from '~/core/enums/custom-headers.enum';
import { IncomingRequestService } from '~/core/services/incoming-request/incoming-request.service';

import { environment } from 'src/environments/environment';

import { AuthRoutingModule } from './auth-routing.module';
import { AuthPageLogoutComponent } from './components/pages/logout/logout.component';
import { AuthPageRedirectionComponent } from './components/pages/redirection/redirection.component';
import { AuthPageWaitingComponent } from './components/pages/waiting/waiting.component';
import { ShowLoggedDirective } from './directives/show-logged.directive';
import { ShowUnloggedDirective } from './directives/show-unlogged.directive';
import {
  CONNECT_CLIENT_ID,
  CONNECT_DISCOVERY_DOCUMENT,
  CONNECT_ISSUER,
  CONNECT_LOGOUT_REDIRECTION_URI,
  CONNECT_REDIRECT_URI,
} from './providers/connect.provider';
import {
  FED_CLIENT_ID,
  FED_DISCOVERY_DOCUMENT,
  FED_ISSUER,
  FED_REDIRECT_URI,
} from './providers/fed.provider';
import { AuthService } from './services/auth/auth.service';

const ACCESS_TOKEN = 'x_access_token';
const ACCESS_TOKEN_KEY = makeStateKey<string>(ACCESS_TOKEN);

export const LOCAL_STORAGE = new InjectionToken<string>('local storage');
export const SESSION_STORAGE = new InjectionToken<string>('session storage');

/**
 * Authentication
 */
@NgModule({
  declarations: [
    ShowLoggedDirective,
    ShowUnloggedDirective,
    AuthPageWaitingComponent,
    AuthPageRedirectionComponent,
    AuthPageLogoutComponent,
  ],
  imports: [
    AuthRoutingModule,
    CommonModule,
    CoreModule,
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: [environment.applications.activities.url],
        sendAccessToken: true,
      },
    }),
  ],
  exports: [ShowLoggedDirective, ShowUnloggedDirective],
  providers: [
    AuthService,

    /**
     * FED
     */
    { provide: FED_ISSUER, useValue: environment.auth.fed.issuer },
    { provide: FED_CLIENT_ID, useValue: environment.auth.fed.clientId },
    { provide: FED_REDIRECT_URI, useValue: environment.auth.fed.redirectUri },
    {
      provide: FED_DISCOVERY_DOCUMENT,
      useValue: environment.auth.fed.discoveryDocument,
    },

    /**
     * Connect
     */
    { provide: CONNECT_ISSUER, useValue: environment.auth.connect.issuer },
    { provide: CONNECT_CLIENT_ID, useValue: environment.auth.connect.clientId },
    {
      provide: CONNECT_REDIRECT_URI,
      useValue: environment.auth.connect.redirectUri,
    },
    {
      provide: CONNECT_DISCOVERY_DOCUMENT,
      useValue: environment.auth.connect.discoveryDocument,
    },
    {
      provide: CONNECT_LOGOUT_REDIRECTION_URI,
      useValue: environment.auth.connect.logoutRedirectionUrl,
    },
    {
      provide: LOCAL_STORAGE,
      useFactory: (platformId: string) =>
        isPlatformBrowser(platformId) ? localStorage : new MemoryStorage(),
      deps: [PLATFORM_ID],
    },
    {
      provide: SESSION_STORAGE,
      useFactory: (platformId: string) =>
        isPlatformBrowser(platformId) ? sessionStorage : new MemoryStorage(),
      deps: [PLATFORM_ID],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (authService: AuthService, platformId: string) => {
        // no auth configured in SSR
        // Don't start auth process in iframe to avoid the infinite creation of iframes with the silent refresh.
        if (!isPlatformBrowser(platformId) || !!window.frameElement) {
          return () => {};
        }

        return () => {
          return authService.initialize().subscribe();
        };
      },
      multi: true,
      deps: [AuthService, PLATFORM_ID],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (
        platformId: string,
        transferState: TransferState,
        incomingRequestService: IncomingRequestService,
        localStorage: Storage,
        sessionStorage: Storage
      ) => {
        return () => {
          /**
           * If server-side, retrieve token header from incoming request
           * and persist it inside the transfer state object.
           */
          if (isPlatformServer(platformId)) {
            transferState.set<string>(
              ACCESS_TOKEN_KEY,
              incomingRequestService.getHeader(
                CustomHeadersEnum.X_APP_AUTH,
                null
              )
            );

            return;
          }

          /**
           * If client-side, retrieve token from transfer state.
           * If its not null, set it in the dom storage.
           */
          const token = transferState.get<string>(ACCESS_TOKEN_KEY, null);

          if (!token) {
            return;
          }

          localStorage.setItem(
            AUTH_ISSUER_KEY,
            environment.auth.connect.issuer
          );
          sessionStorage.setItem(ACCESS_TOKEN, token);
        };
      },
      multi: true,
      deps: [
        PLATFORM_ID,
        TransferState,
        IncomingRequestService,
        LOCAL_STORAGE,
        SESSION_STORAGE,
      ],
    },
  ],
})
export class AuthModule {}
